// `common/filters/time-ago.filter.ng1.ts` — one line upstream, `moment(timestamp).fromNow()`,
// and sixty here because moment is not a dependency of this repo and adding a date library to
// render one string in one card is not the trade the port should make.
//
// ⚠️ THIS IS NOT THE SAME FUNCTION AS `timeSince`, and the two sit a metre apart in the same
// folder. `timeSince` is careero's OWN helper (`locale.service.ng1.ts`) — "31mos ago", with the
// one-and-two-years-in-months rule. This is moment's, which rounds and switches units at
// thresholds nobody would guess: 44 seconds is "a few seconds ago" and 45 is "45 seconds ago";
// 22 hours is "22 hours ago" and 23 is "a day ago". Pick by which upstream filter the template
// actually pipes through, not by which name reads better.

/** moment's `relativeTime` thresholds, verbatim: `{ ss: 44, s: 45, m: 45, h: 22, d: 26, M: 11 }`. */
const THRESHOLD = { ss: 44, s: 45, m: 45, h: 22, d: 26, M: 11 } as const

/**
 * `daysToMonths` — moment's own approximation, 400 Gregorian years being 146097 days:
 * `days * 4800 / 146097`, a month of 30.436875 days rather than 30.
 */
function daysToMonths(days: number): number {
  return (days * 4800) / 146097
}

/**
 * `moment(timestamp).fromNow()` for the EN locale and a past timestamp.
 *
 * ⚠️ EVERY UNIT IS ROUNDED FROM THE WHOLE DURATION, NOT CARRIED DOWN FROM THE ONE ABOVE. moment
 * expresses the full duration in each unit and rounds each independently, then takes the first
 * branch that matches — so 90 minutes is `hours = round(1.5) = 2` and reads "2 hours ago",
 * never "an hour ago". Deriving hours from a remainder after minutes gives a different answer.
 *
 * `now` is a parameter so this is testable; upstream reads the clock.
 */
export function timeAgo(timestamp: number | null | undefined, now: number = Date.now()): string {
  if (timestamp === undefined || timestamp === null) return ''

  const ms = now - timestamp
  const seconds = Math.round(ms / 1000)
  const minutes = Math.round(ms / 60_000)
  const hours = Math.round(ms / 3_600_000)
  const days = Math.round(ms / 86_400_000)
  const months = Math.round(daysToMonths(ms / 86_400_000))
  const years = Math.round(daysToMonths(ms / 86_400_000) / 12)

  /** `past: '%s ago'` — moment's EN locale, applied to whichever phrase wins below. */
  const ago = (phrase: string): string => `${phrase} ago`

  if (seconds <= THRESHOLD.ss) return ago('a few seconds')
  // ⚠️ UNREACHABLE AT THE DEFAULT THRESHOLDS, AND THAT IS MOMENT'S SHAPE, NOT A MISTAKE HERE:
  // it needs `seconds > 44 && seconds < 45`, which no whole second satisfies. The branch exists
  // for locales that lower `ss` — EN never counts seconds, it goes straight to "a minute ago".
  // Deleting it would make the next person deriving these thresholds think EN says "45 seconds".
  if (seconds < THRESHOLD.s) return ago(`${String(seconds)} seconds`)
  if (minutes <= 1) return ago('a minute')
  if (minutes < THRESHOLD.m) return ago(`${String(minutes)} minutes`)
  if (hours <= 1) return ago('an hour')
  if (hours < THRESHOLD.h) return ago(`${String(hours)} hours`)
  if (days <= 1) return ago('a day')
  if (days < THRESHOLD.d) return ago(`${String(days)} days`)
  if (months <= 1) return ago('a month')
  if (months < THRESHOLD.M) return ago(`${String(months)} months`)
  if (years <= 1) return ago('a year')
  return ago(`${String(years)} years`)
}
